import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { appointmentActions } from 'store';
import { FindLoad, Images, User, useSecondaryModal } from 'utils';
import { CloseButton, StatusCard, TextRow } from 'components';
import { ScheduleDetails } from '../../../schedule';

const ACTION_TYPE = 'GET_APPOINTMENT_BY_FILTERS';

const TABS = {
    UPCOMING: 'UPCOMING',
    OVERDUE: 'OVERDUE',
};

const ApptSkeleton = () => (
    <>
        {[1, 2, 3].map((i) => (
            <div key={i} className="appt-drawer-card appt-card-skeleton">
                <div className="appt-card-skeleton-header">
                    <div className="appt-card-skeleton-line" style={{ width: '90px', height: '14px' }} />
                    <div className="appt-card-skeleton-line" style={{ width: '60px', height: '22px', borderRadius: '12px' }} />
                </div>
                <div className="appt-card-skeleton-body">
                    <div className="appt-card-skeleton-row">
                        <div className="appt-card-skeleton-icon" />
                        <div className="appt-card-skeleton-line" style={{ width: '75%', height: '12px' }} />
                    </div>
                    <div className="appt-card-skeleton-row">
                        <div className="appt-card-skeleton-icon" />
                        <div className="appt-card-skeleton-line" style={{ width: '50%', height: '12px' }} />
                    </div>
                </div>
            </div>
        ))}
    </>
);

export const ApptDrawer = ({ handleClose }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const loading = FindLoad(ACTION_TYPE);
    const upcomingAppts = useSelector((state) => state.appointment.appts);
    const { userInfo } = User();
    const { openSecondary } = useSecondaryModal();
    const [activeTab, setActiveTab] = useState(TABS.UPCOMING);

    const handleLoad = (loading) => {
        dispatch(appointmentActions.getAppointmentByFilters({
            startDate: moment().subtract(7, 'days').format('YYYY-MM-DD'),
            endDate: moment().add(7, 'days').format('YYYY-MM-DD'),
            statuses: ['PENDING'],
            staff: userInfo?.id,
            limit: 50,
            skip: 0,
        }, loading));
    };

    useEffect(() => {
        handleLoad();
    }, []);

    const handleApptClick = (appt) => {
        openSecondary(<ScheduleDetails currentId={appt?.id} getAfterSuccess={() => handleLoad('noLoad')} />);
    };

    const allAppts = upcomingAppts?.appts || [];

    const upcomingList = allAppts.filter((appt) => {
        const start = moment.utc(appt?.startTime);
        return start.isSameOrAfter(moment(), 'day');
    });

    const overdueList = allAppts.filter((appt) => {
        const start = moment.utc(appt?.startTime);
        return start.isBefore(moment(), 'day');
    });

    const currentAppts = activeTab === TABS.UPCOMING ? upcomingList : overdueList;
    const overdueCount = overdueList.length;

    const formatDate = (appt) => {
        const start = moment.utc(appt?.startTime);
        const end = moment.utc(appt?.endTime);
        return `${start.format('dddd')}, ${start.format('MM/DD/YYYY')}, ${start.format('h:mma')}-${end.format('h:mma')}`;
    };

    const getDaysAgo = (appt) => {
        const start = moment.utc(appt?.startTime).startOf('day');
        const today = moment().startOf('day');
        return today.diff(start, 'days');
    };

    return (
        <div className="appt-drawer-container">
            <div className="appt-drawer-header">
                <div className="appt-drawer-header-text">
                    <h3>My Appointments</h3>
                    <span className="appt-drawer-subtitle">Next 7 days</span>
                </div>
                <div className="appt-drawer-header-actions">
                    <button
                        className="appt-drawer-view-all-btn"
                        onClick={() => {
                            handleClose();
                            navigate('/schedule');
                        }}
                    >
                        View all
                    </button>
                    <CloseButton styles={{ width: '35px', height: '35px' }} handleCLic={handleClose} />
                </div>
            </div>

                <div className="my-tasks-tabs-wrapper">
                    <button
                        className={`my-tasks-tab-btn ${activeTab === TABS.UPCOMING ? 'my-tasks-tab-btn-active' : ''}`}
                        onClick={() => setActiveTab(TABS.UPCOMING)}
                    >
                        Upcoming
                    </button>
                    <button
                        className={`my-tasks-tab-btn ${activeTab === TABS.OVERDUE ? 'my-tasks-tab-btn-active' : ''}`}
                        onClick={() => setActiveTab(TABS.OVERDUE)}
                    >
                        Overdue
                        {overdueCount > 0 && (
                            <span
                                className="my-tasks-tab-count"
                                style={{ background: '#FEF3F2', color: '#B42318' }}
                            >
                            {overdueCount}
                        </span>
                        )}
                    </button>
                </div>

            <div className="appt-drawer-list">
                {loading?.length ? (
                    <ApptSkeleton />
                ) : currentAppts.length === 0 ? (
                    <div className="appt-drawer-empty">
                        <img
                            src={Images.noUpcomingAppointment || Images.calendarToday}
                            alt="no appointments"
                            className="appt-drawer-empty-img"
                        />
                        <p>{activeTab === TABS.UPCOMING ? 'No upcoming appointments' : 'No overdue appointments'}</p>
                    </div>
                ) : (
                    currentAppts.map((appt, i) => (
                        <div
                            key={appt.id || i}
                            className="upcoming-appointments-card-wrapper appt-drawer-card"
                            onClick={() => handleApptClick(appt)}
                            style={appt?.authBillable === false ? { background: 'repeating-linear-gradient(135deg, #E4E7EC 0px, #E4E7EC 1px, #FEFCE8 1px, #FEFCE8 14px)' } : {}}
                        >
                            <div className="upcoming-appointments-card-header">
                                <p className="upcoming-appointments-card-header-title">{appt?.displayId}</p>
                                <div className="upcoming-appointments-card-header-status-arrow">
                                    <StatusCard status={activeTab === TABS.OVERDUE ? 'OVERDUE' : 'PENDING'} />
                                    <img style={{transform: 'rotate(-90deg)'}} src={Images.accordArrowBlack} alt="arrow" />
                                </div>
                            </div>

                            <div className="upcoming-appointments-card-body">
                                <div className="upcoming-appointments-card-item">
                                    <div className="upcoming-appointments-card-item-svg">
                                        <img
                                            style={{filter: 'brightness(0) saturate(100%) invert(21%) sepia(12%) saturate(1505%) hue-rotate(178deg) brightness(93%) contrast(84%)'}}
                                            src={Images.calendar}
                                            alt="calendar"
                                        />
                                    </div>
                                    <p className="upcoming-appointments-card-item-text">
                                        <TextRow name={formatDate(appt)} />
                                    </p>
                                </div>

                                {(appt?.client?.firstName || appt?.client?.lastName) && (
                                    <div className="upcoming-appointments-card-item">
                                        <div className="upcoming-appointments-card-item-svg">
                                            <img
                                                style={{filter: 'brightness(0) saturate(100%) invert(21%) sepia(12%) saturate(1505%) hue-rotate(178deg) brightness(93%) contrast(84%)'}}
                                                src={Images.client}
                                                alt="client"
                                            />
                                        </div>
                                        <p className="upcoming-appointments-card-item-text">
                                            <TextRow
                                                name={`${appt?.client?.firstName || ''} ${appt?.client?.lastName || ''}`}
                                            />
                                        </p>
                                    </div>
                                )}

                                {activeTab === TABS.OVERDUE && (
                                    <div className="upcoming-appointments-card-overdue-info">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12"
                                             viewBox="0 0 12 12" fill="none">
                                            <path
                                                d="M5.75 2.75V5.75L7.75 6.75M10.75 5.75C10.75 8.51142 8.51142 10.75 5.75 10.75C2.98858 10.75 0.75 8.51142 0.75 5.75C0.75 2.98858 2.98858 0.75 5.75 0.75C8.51142 0.75 10.75 2.98858 10.75 5.75Z"
                                                stroke="#FB6514" stroke-width="1.5" stroke-linecap="round"
                                                stroke-linejoin="round" />
                                        </svg>
                                        <p>Scheduled {getDaysAgo(appt)} days ago</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};
